import {BellIcon} from '@/components/common/icons/BellIcon'
import {GridIcon} from '@/components/common/icons/GridIcon'
import {HomeIcon} from '@/components/common/icons/HomeIcon'
import {LockIcon} from '@/components/common/icons/LockIcon'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {NextAuthErrors} from '@/utils/auth/constants'
import {useLanguage} from '@/utils/i18n/language'
import {getRouteToPrivacyPolicy, routeTo} from '@/utils/router'
import {AuthPageType} from '@/utils/user/constants'

import {getAuthContent} from './authenticationContent'

export const useAuthContent = () => {
  const language = useLanguage()
  const content = getAuthContent()

  return {
    ...content,
    signUp: {
      title: 'authentication.signup-title',
      confirmPasswordLabel: 'authentication.confirm-password-title',
      buttonText: 'authentication.create-account-title',
      signInPromoText: 'authentication.signup-login-promo',
      signInLinkText: 'authentication.signup-login-link',
      signUpNote: 'authentication.signup-note',
      termsLinkUrl: routeTo.accountTermsAndConditions(),
      privacyPolicyLinkUrl: getRouteToPrivacyPolicy(language),
    },
    signIn: {
      title: 'authentication.login-title',
      forgotPasswordLinkUrl: routeTo.portal.forgotPassword(),
      forgotPasswordLinkText: 'authentication.login-forgot-password',
      errors: {
        [NextAuthErrors.AccountDoesNotExist]: 'authentication.error-sign-up',
        [NextAuthErrors.ActivateAccount]: 'authentication.error-activate-account',
        [NextAuthErrors.CredentialsSignin]: 'authentication.login-invalid-credentials',
        [NextAuthErrors.Default]: 'authentication.login-error',
      },
      buttonText: 'authentication.login-button',
      loginWithPasswordButtonText: 'authentication.login-with-password-button',
      orDividerText: 'authentication.or-divider',
      magicLink: {
        title: 'authentication.login-by-magic-link-title',
        description: 'authentication.login-by-magic-link-desc',
        buttonText: 'authentication.send-magic-link-button',
        success: {
          title: 'authentication.login-by-magic-link-request-success-title',
          description: 'authentication.login-by-magic-link-request-success-description',
        },
      },
      formButtonText: 'page.menu-login',
      signUpPromoText: 'authentication.login-signup-promo',
      signUpLinkText: 'authentication.create-account-title',
      mandatory: {
        imageSrc: '/images/icons/drafting.svg',
        imageAlt: 'Document with a pen',
        content: {
          [AuthPageType.general]: {
            title: 'authentication.mandatory-login-title',
            description: 'authentication.mandatory-login-description',
          },
          [AuthPageType.sensitiveData]: {
            title: 'authentication.mandatory-login-sensitive-data-title',
            description: 'authentication.mandatory-login-sensitive-data-description',
          },
        },
        benefits: [
          {
            icon: <BellIcon color={theme.palette.text.primary} size={IconSize.large} />,
            title: 'authentication.mandatory-login-benefit-title-0',
            description: 'authentication.mandatory-login-benefit-description-0',
          },
          {
            icon: <GridIcon color={theme.palette.text.primary} size={IconSize.large} />,
            title: 'authentication.mandatory-login-benefit-title-1',
            description: 'authentication.mandatory-login-benefit-description-1',
          },
          {
            icon: <LockIcon />,
            title: 'authentication.mandatory-login-benefit-title-2',
            description: 'authentication.mandatory-login-benefit-description-2',
          },
        ],
      },
    },
    successLoginByMagicLink: {
      ...content.successLoginByMagicLink,
      invalidLinkButtonIcon: <HomeIcon color={theme.palette.background.default} />,
    },
    formAuth: {
      createAccountTitle: 'authentication.create-account-optional-title',
      createAccountCheckbox: 'authentication.create-account-checkbox',
      haveAccountTitle: 'authentication.have-account-title',
      haveAccountActivateTitle: 'authentication.have-account-activate-title',
    },
    accountActivation: {
      title: 'authentication.account-activation-modal-header',
      descriptionLine1: 'authentication.account-activation-modal-description-line1',
      descriptionLine2: 'authentication.account-activation-modal-description-line2',
    },
    successActivation: {
      ...content.successActivation,
      invalidLinkButtonIcon: <HomeIcon color={theme.palette.background.default} />,
    },
    emailLabel: 'contact-email',
    emailPlaceholder: 'contact-email-placeholder',
    passwordLabel: 'password-label',
    passwordPlaceholder: 'enter-password-placeholder',
    noAccess: {
      imageSrc: '/images/icons/document-with-lock-icon.svg',
      imageAlt: 'Document with lock icon',
      title: 'authentication.no-access-error-title',
      description: 'authentication.no-access-error-desc',
      descriptionWithEmail: 'authentication.no-access-error-desc-with-email',
      buttonText: 'authentication.no-access-error-button',
    },
    changePasswordButton: 'authentication.change-password-title',
    setPasswordError: 'authentication.password-error-api',
    logoutTitle: 'page.menu-user-logout',
  }
}
